import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { LinkIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  getApiAdminvideoNoticesGetCollectionQueryKey,
  useApiAdminvideoNoticesIdPatch,
  useApiAdminvideoNoticesPost
} from '@r/api/generated/client.ts';
import type { VideoNoticeVideoNoticeCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import {
  apiAdminvideoNoticesPostBody,
  apiAdminvideoNoticesPostBodyCategoryMax,
  apiAdminvideoNoticesPostBodyTitleMax,
  apiAdminvideoNoticesPostBodyVideoUrlMax
} from '@r/api/generated-zod/index.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { Button } from '@ui/button.tsx';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';

/**
 * Étend le schéma généré par l’API avec des messages de validation traduits,
 * tout en conservant les longueurs maximales imposées par le backend.
 */
const getVideoNoticeFormSchema = (t: ReturnType<typeof useTranslation>['t']) =>
  apiAdminvideoNoticesPostBody.extend({
    title: z
      .string()
      .trim()
      .min(1, t('fiddy.admin.video.notices.form.title.required'))
      .max(apiAdminvideoNoticesPostBodyTitleMax, t('fiddy.admin.video.notices.form.title.max')),
    videoUrl: z
      .string()
      .trim()
      .min(1, t('fiddy.admin.video.notices.form.url.required'))
      .url(t('fiddy.admin.video.notices.form.url.invalid'))
      .max(apiAdminvideoNoticesPostBodyVideoUrlMax, t('fiddy.admin.video.notices.form.url.max')),
    category: z
      .string()
      .trim()
      .max(
        apiAdminvideoNoticesPostBodyCategoryMax,
        t('fiddy.admin.video.notices.form.category.max')
      )
      .nullish()
  });

type VideoNoticeFormValues = z.infer<ReturnType<typeof getVideoNoticeFormSchema>>;

interface VideoNoticeFormProps {
  onSuccess: () => void;
  videoNotice?: VideoNoticeVideoNoticeCollectionReadModel | null;
}

/**
 * Affiche le formulaire de création ou d’édition d’une notice vidéo.
 * La présence d’une notice détermine automatiquement si le formulaire appelle le POST ou le PATCH.
 */
export function VideoNoticeForm({ onSuccess, videoNotice }: VideoNoticeFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const isEditing = Boolean(videoNotice);
  const formSchema = getVideoNoticeFormSchema(t);
  const form = useForm<VideoNoticeFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      title: videoNotice?.title ?? '',
      videoUrl: videoNotice?.videoUrl ?? '',
      category: videoNotice?.category ?? '',
      description: videoNotice?.description ?? ''
    }
  });

  /**
   * Rafraîchit toutes les listes de notices puis ferme et réinitialise le formulaire.
   */
  const handleSuccess = async () => {
    await queryClient.invalidateQueries({
      queryKey: getApiAdminvideoNoticesGetCollectionQueryKey()
    });
    toast.success(
      t(
        isEditing
          ? 'fiddy.admin.video.notices.edit.success'
          : 'fiddy.admin.video.notices.create.success'
      )
    );
    form.reset();
    onSuccess();
  };

  const createVideoNotice = useApiAdminvideoNoticesPost({
    mutation: {
      onSuccess: handleSuccess,
      onError: (error) => {
        showOrvalErrorToast(error, t('fiddy.admin.video.notices.create.error'));
      }
    }
  });

  const updateVideoNotice = useApiAdminvideoNoticesIdPatch({
    mutation: {
      onSuccess: handleSuccess,
      onError: (error) => {
        showOrvalErrorToast(error, t('fiddy.admin.video.notices.edit.error'));
      }
    }
  });

  /**
   * Normalise les champs optionnels avant d’envoyer la création ou la modification à l’API.
   */
  const onSubmit = (values: VideoNoticeFormValues) => {
    const data = {
      title: values.title,
      videoUrl: values.videoUrl,
      category: values.category?.trim() || null,
      description: values.description?.trim() || null
    };

    if (videoNotice) {
      updateVideoNotice.mutate({
        id: String(videoNotice.id),
        data
      });
      return;
    }

    createVideoNotice.mutate({ data });
  };

  const isPending = createVideoNotice.isPending || updateVideoNotice.isPending;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('fiddy.admin.video.notices.form.title.label')} *</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t('fiddy.admin.video.notices.form.title.placeholder')}
                  error={Boolean(fieldState.error)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="videoUrl"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('fiddy.admin.video.notices.form.url.label')} *</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="url"
                  placeholder={t('fiddy.admin.video.notices.form.url.placeholder')}
                  suffix={<LinkIcon className="size-4" />}
                  error={Boolean(fieldState.error)}
                />
              </FormControl>
              <p className="text-xs text-muted-foreground">
                {t('fiddy.admin.video.notices.form.url.hint')}
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="category"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('fiddy.admin.video.notices.form.category.label')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value ?? ''}
                  placeholder={t('fiddy.admin.video.notices.form.category.placeholder')}
                  error={Boolean(fieldState.error)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('fiddy.admin.video.notices.form.description.label')}</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  value={field.value ?? ''}
                  placeholder={t('fiddy.admin.video.notices.form.description.placeholder')}
                  className="min-h-28"
                  error={Boolean(fieldState.error)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-center border-t pt-5">
          <Button
            type="submit"
            className="w-full sm:max-w-72"
            loading={isPending}
            disabled={!form.formState.isValid}
          >
            {t(
              isEditing
                ? 'fiddy.admin.video.notices.form.edit.submit'
                : 'fiddy.admin.video.notices.form.create.submit'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}
